import { GA4_ID } from "./env.ts";
import { getStatsSessionNumber } from "./session.ts";
import { StoreValue, deleteStoredValue, getStoredValue, setStoredValue } from "./settings.ts";
import { round } from "./utils.ts";

declare let gtag: Function; // eslint-disable-line

const CURRENCY = "USD"; // TODO change to "MIN"

export enum Event {
    /** A page view*/
    PageView = "page_view",
    /** An error / exception*/
    Error = 'exception',
    /** Share the app/link*/
    Share = 'share',
    /** Log this event when a user joins a group */
    JoinGroup = 'join_group',
    /** Log this event when a coherence session starts */
    SessionStart = 'add_to_cart',
    /** Log this event when a coherence session ends */
    SessionEnd = 'purchase',
    /** An item selected in the UI */
    SelectItem = 'select_item',
}

export function recordAnalytic(event: Event | string, data: Record<string, unknown> = {}) {
    if (typeof gtag !== 'undefined') {
        gtag('event', event, data);
    }
}

export function recordEvent(event: Event | string, data: Record<string, unknown> = {}) {
    const hasData = data && Object.keys(data).length > 0;
    if (hasData) {
        console.debug(`analytics:${event}: ${JSON.stringify(data)}`);
    } else {
        console.debug(`analytics:${event}`);
    }
    recordAnalytic(event, data);
}

const SESSION_ID_NAME_DATA = {
    item_id: "coherence_session",
    item_name: "Coherence Session",
}

/**
 * Update user consent to report anymous analytics
 *
 * @param {boolean} enabled use false to disable analytics reporting
 */
export function updateConsent(enabled: boolean) {
    if (!enabled) { // save disabled in localstorage so index.html/gtag can know
        setStoredValue(StoreValue.analytics, { 'disabled': true });
    } else {
        deleteStoredValue(StoreValue.analytics); // remove value (enabled)
    }
    // update analytics consent 
    const consent = enabled ? 'granted' : 'denied';
    console.debug(`Analytics consent: ${enabled}`);
    gtag('consent', 'update', {
        'ad_personalization': 'denied', // no ads personalization
        'ad_user_data': 'denied',       // no ads user data
        'analytics_storage': consent,
        'ad_storage': consent,
    });
}

/**
 * Get the google analytics client ID
 */
export const getGA4ClientId = (): Promise<string> => {
    return new Promise((resolve, reject) => {
        // 1. Check if GA exists
        if (typeof gtag !== 'function') {
            return reject('GA_NOT_INITIALIZED');
        }
        const timeout = setTimeout(() => reject('timeout error'), 300); // timeout after 300ms
        gtag('get', GA4_ID, 'client_id', (clientId: number) => {
            clearTimeout(timeout);
            resolve(clientId.toString()); // ensure it's a string
        });
    });
}


/**
 * Are analytics enabled
 */
export function isEnabled() {
    return getStoredValue(StoreValue.analytics) === null; // empty means disabled
}

export function recordEventSessionStart(lengthMinutes: number,
    inhaleTime: number, exhaleTime: number) {
    lengthMinutes = round(lengthMinutes, 2);
    inhaleTime = round(inhaleTime, 1);
    exhaleTime = round(exhaleTime, 1);
    const itemCategory = `${inhaleTime}s-${exhaleTime}s`;

    const eventData = {
        currency: CURRENCY,
        value: lengthMinutes,
        items: [{
            ...SESSION_ID_NAME_DATA,
            item_category: itemCategory,
            price: lengthMinutes,
            quantity: 1,
        }]
    };
    recordEvent(Event.SessionStart, eventData);
    // immediately fire the begin_checkout to measure session commitment if they drop off
    recordEvent('begin_checkout', eventData);
}

export function recordEventSessionEnd(lengthMinutes: number, inhaleTime: number,
    exhaleTime: number, fullSession: boolean,) {
    lengthMinutes = round(lengthMinutes, 2);
    inhaleTime = round(inhaleTime, 1);
    exhaleTime = round(exhaleTime, 1);
    const itemCategory = `${inhaleTime}s-${exhaleTime}s`;
    const transactionId = (fullSession ? "coherence_full_" : "coherence_partial_") + Date.now();
    const variant = fullSession ? "Complete" : "Partial";

    recordEvent(Event.SessionEnd, {
        currency: CURRENCY,
        value: lengthMinutes,
        transaction_id: transactionId,
        // Custom Event-Scoped Parameter
        session_count_total: getStatsSessionNumber(), // store session number
        items: [{
            ...SESSION_ID_NAME_DATA,
            item_category: itemCategory,
            item_variant: variant,
            price: lengthMinutes,
            quantity: 1
        }]
    });
}
